import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { FaUser, FaImage, FaEye, FaInstagram, FaTwitter, FaFacebook, FaUpload } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';

const SettingsContainer = styled.div`
  padding: 120px 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
  color: white;
`;

const HeaderSection = styled.div`
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 10px;
`;

const SettingsCard = styled.div`
  background: #1a102a;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 0;
  margin-bottom: 20px;
  overflow: hidden;
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
  transition: background 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const SettingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
`;

const SettingInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SettingTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin: 0;
`;

const SettingDescription = styled.p`
  font-size: 0.85rem;
  color: var(--secondary-color);
  margin: 0;
  margin-top: 2px;
`;

const SettingRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--secondary-color);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: var(--primary-color);
    border-color: var(--gradient-cyan);
  }

  &.primary {
    background: var(--gradient-cyan);
    color: #111;
    border-color: var(--gradient-cyan);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(78, 205, 196, 0.3);
    }
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 0.9rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--primary-color);
  cursor: pointer;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: var(--gradient-cyan);
  }

  option {
    background: #1a102a;
    color: white;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a102a;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 1.1rem;
  color: var(--primary-color);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--secondary-color);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--primary-color);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--primary-color);

  &:focus {
    outline: none;
    border-color: var(--gradient-cyan);
  }

  &::placeholder {
    color: var(--secondary-color);
  }
`;

const SocialMediaGrid = styled.div`
  display: grid;
  gap: 15px;
`;

const SocialInput = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border-color);
  border-radius: 8px;

  &:focus-within {
    border-color: var(--gradient-cyan);
  }
`;

const SocialIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
`;

const SocialInputField = styled.input`
  flex: 1;
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.9rem;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--secondary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 25px;
`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    profile_visibility: 'public',
    avatar_url: '',
    banner_url: '',
    instagram_url: '',
    twitter_url: '',
    facebook_url: '',
    tiktok_url: ''
  });

  useEffect(() => {
    checkAuthAndLoadProfile();
  }, []);

  const checkAuthAndLoadProfile = async () => {
    try {
      // Önce auth kontrolü yap
      const authResponse = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
        credentials: 'include'
      });

      if (!authResponse.ok || !((await authResponse.json()).success)) {
        // Giriş yapılmamışsa ana sayfaya yönlendir
        navigate('/');
        return;
      }

      // Auth OK ise profili yükle
      await loadUserProfile();
    } catch (error) {
      console.error('Auth kontrolü hatası:', error);
      navigate('/');
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.GET_USER_PROFILE), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        // Eğer data.user varsa onu kullan, yoksa data'nın kendisini kullan
        const userData = data.user || data;
        
        setUserProfile(userData);
        
        const newFormData = {
          username: userData.username || '',
          profile_visibility: userData.profile_visibility || 'public',
          avatar_url: userData.avatar_url || '',
          banner_url: userData.banner_url || '',
          instagram_url: userData.instagram_url || '',
          twitter_url: userData.twitter_url || '',
          facebook_url: userData.facebook_url || '',
          tiktok_url: userData.tiktok_url || ''
        };
        
        setFormData(newFormData);
      } else {
        console.error('API yanıt hatası:', response.status);
      }
    } catch (error) {
      console.error('Profil yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (modalType) => {
    setShowModal(modalType);
  };

  const closeModal = () => {
    setShowModal(null);
    // Form verilerini sıfırla
    setFormData({
      username: userProfile?.username || '',
      profile_visibility: userProfile?.profile_visibility || 'public',
      avatar_url: userProfile?.avatar_url || '',
      banner_url: userProfile?.banner_url || '',
      instagram_url: userProfile?.instagram_url || '',
      twitter_url: userProfile?.twitter_url || '',
      facebook_url: userProfile?.facebook_url || '',
      tiktok_url: userProfile?.tiktok_url || ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVisibilityChange = async (visibility) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_PROFILE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          profile_visibility: visibility
        })
      });

      if (response.ok) {
        setUserProfile(prev => ({
          ...prev,
          profile_visibility: visibility
        }));
        setFormData(prev => ({
          ...prev,
          profile_visibility: visibility
        }));
      }
    } catch (error) {
      console.error('Görünürlük ayarı güncellenirken hata:', error);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Sadece resim dosyaları yükleyebilirsiniz');
      return;
    }

    // Geçici olarak file'ı okuyup preview için URL oluştur
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        [`${type}_url`]: e.target.result,
        [`${type}_file`]: file // Dosyayı da sakla
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      const updateData = {};

      if (showModal === 'username') {
        updateData.username = formData.username;
      } else if (showModal === 'avatar') {
        // Eğer dosya seçildiyse önce upload et
        if (formData.avatar_file) {
          const fileFormData = new FormData();
          fileFormData.append('image', formData.avatar_file);
          fileFormData.append('type', 'avatar');

          const uploadResponse = await fetch(getApiUrl(API_ENDPOINTS.UPLOAD_IMAGE), {
            method: 'POST',
            body: fileFormData,
            credentials: 'include'
          });

          const uploadData = await uploadResponse.json();
          if (uploadData.success) {
            updateData.avatar_url = uploadData.url;
          } else {
            alert(uploadData.message || 'Yükleme başarısız');
            return;
          }
        } else {
          updateData.avatar_url = formData.avatar_url;
        }
      } else if (showModal === 'banner') {
        // Eğer dosya seçildiyse önce upload et
        if (formData.banner_file) {
          const fileFormData = new FormData();
          fileFormData.append('image', formData.banner_file);
          fileFormData.append('type', 'banner');

          const uploadResponse = await fetch(getApiUrl(API_ENDPOINTS.UPLOAD_IMAGE), {
            method: 'POST',
            body: fileFormData,
            credentials: 'include'
          });

          const uploadData = await uploadResponse.json();
          if (uploadData.success) {
            updateData.banner_url = uploadData.url;
          } else {
            alert(uploadData.message || 'Yükleme başarısız');
            return;
          }
        } else {
          updateData.banner_url = formData.banner_url;
        }
      } else if (showModal === 'social') {
        updateData.instagram_url = formData.instagram_url;
        updateData.twitter_url = formData.twitter_url;
        updateData.facebook_url = formData.facebook_url;
        updateData.tiktok_url = formData.tiktok_url;
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_PROFILE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await loadUserProfile();
        setShowModal(null);
      } else {
        alert('Güncelleme sırasında hata oluştu');
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      alert('Güncelleme sırasında hata oluştu');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <SettingsContainer>
          <HeaderSection>
            <Title>Ayarlar</Title>
          </HeaderSection>
          <div style={{ textAlign: 'center', color: 'var(--secondary-color)' }}>
            Yükleniyor...
          </div>
        </SettingsContainer>
      </>
    );
  }

  return (
    <>
      <Header />
      <SettingsContainer>
        <HeaderSection>
          <Title>Profil</Title>
        </HeaderSection>

      <SettingsCard>
        <SettingItem>
          <SettingLeft>
            <IconWrapper>
              <FaUser />
            </IconWrapper>
            <SettingInfo>
              <SettingTitle>Kullanıcı adını değiştir</SettingTitle>
            </SettingInfo>
          </SettingLeft>
          <SettingRight>
            <ActionButton className="primary" onClick={() => openModal('username')}>
              Kullanıcı adını değiştir
            </ActionButton>
          </SettingRight>
        </SettingItem>

        <SettingItem>
          <SettingLeft>
            <IconWrapper>
              <FaImage />
            </IconWrapper>
            <SettingInfo>
              <SettingTitle>Profil resimlerini değiştir</SettingTitle>
            </SettingInfo>
          </SettingLeft>
          <SettingRight>
            <ActionButton onClick={() => openModal('avatar')}>
              Profil avatarını değiştir
            </ActionButton>
            <ActionButton className="primary" onClick={() => openModal('banner')}>
              Profil afişini değiştir
            </ActionButton>
          </SettingRight>
        </SettingItem>

        <SettingItem>
          <SettingLeft>
            <IconWrapper>
              <FaEye />
            </IconWrapper>
            <SettingInfo>
              <SettingTitle>Profil görünürlüğünü değiştir</SettingTitle>
            </SettingInfo>
          </SettingLeft>
          <SettingRight>
            <Select 
              value={formData.profile_visibility} 
              onChange={(e) => handleVisibilityChange(e.target.value)}
            >
              <option value="public">Herkese açık</option>
              <option value="private">Özel</option>
            </Select>
          </SettingRight>
        </SettingItem>

        <SettingItem>
          <SettingLeft>
            <IconWrapper>
              <FaInstagram />
            </IconWrapper>
            <SettingInfo>
              <SettingTitle>Sosyal medya hesapları</SettingTitle>
              <SettingDescription>Instagram, Twitter, Facebook ve TikTok linklerinizi ekleyin</SettingDescription>
            </SettingInfo>
          </SettingLeft>
          <SettingRight>
            <ActionButton onClick={() => openModal('social')}>Sosyal medya</ActionButton>
          </SettingRight>
        </SettingItem>
      </SettingsCard>

      {/* Username Modal */}
      {showModal === 'username' && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Kullanıcı Adını Değiştir</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Yeni Kullanıcı Adı</Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Yeni kullanıcı adınızı girin"
              />
            </FormGroup>

            <ButtonGroup>
              <ActionButton onClick={closeModal}>İptal</ActionButton>
              <ActionButton className="primary" onClick={handleSave}>Kaydet</ActionButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Images Modal */}
      {showModal === 'images' && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Profil Resimleri</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Avatar URL</Label>
              <Input
                type="url"
                value={formData.avatar_url}
                onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                placeholder="https://example.com/avatar.jpg"
              />
            </FormGroup>

            <FormGroup>
              <Label>Banner URL</Label>
              <Input
                type="url"
                value={formData.banner_url}
                onChange={(e) => handleInputChange('banner_url', e.target.value)}
                placeholder="https://example.com/banner.jpg"
              />
            </FormGroup>

            <ButtonGroup>
              <ActionButton onClick={closeModal}>İptal</ActionButton>
              <ActionButton className="primary" onClick={handleSave}>Kaydet</ActionButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Avatar Modal */}
      {showModal === 'avatar' && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Profil Avatarını Değiştir</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Avatar URL</Label>
              <Input
                type="url"
                value={formData.avatar_url}
                onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                placeholder="https://example.com/avatar.jpg"
              />
            </FormGroup>

            <FormGroup>
              <Label>Bilgisayardan Seç</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'avatar')}
              />
            </FormGroup>

            <ButtonGroup>
              <ActionButton onClick={closeModal}>İptal</ActionButton>
              <ActionButton className="primary" onClick={handleSave}>Kaydet</ActionButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Banner Modal */}
      {showModal === 'banner' && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Profil Afişi Değiştir</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Banner URL</Label>
              <Input
                type="url"
                value={formData.banner_url}
                onChange={(e) => handleInputChange('banner_url', e.target.value)}
                placeholder="https://example.com/banner.jpg"
              />
            </FormGroup>

            <FormGroup>
              <Label>Bilgisayardan Seç</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'banner')}
              />
            </FormGroup>

            <ButtonGroup>
              <ActionButton onClick={closeModal}>İptal</ActionButton>
              <ActionButton className="primary" onClick={handleSave}>Kaydet</ActionButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Social Media Modal */}
      {showModal === 'social' && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Sosyal Medya Hesapları</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            
            <SocialMediaGrid>
              <FormGroup>
                <Label>Instagram</Label>
                <SocialInput>
                  <SocialIcon><FaInstagram /></SocialIcon>
                  <SocialInputField
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                    placeholder="https://instagram.com/username"
                  />
                </SocialInput>
              </FormGroup>

              <FormGroup>
                <Label>Twitter</Label>
                <SocialInput>
                  <SocialIcon><FaTwitter /></SocialIcon>
                  <SocialInputField
                    type="url"
                    value={formData.twitter_url}
                    onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                    placeholder="https://twitter.com/username"
                  />
                </SocialInput>
              </FormGroup>

              <FormGroup>
                <Label>Facebook</Label>
                <SocialInput>
                  <SocialIcon><FaFacebook /></SocialIcon>
                  <SocialInputField
                    type="url"
                    value={formData.facebook_url}
                    onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/username"
                  />
                </SocialInput>
              </FormGroup>

              <FormGroup>
                <Label>TikTok</Label>
                <SocialInput>
                  <SocialIcon><SiTiktok /></SocialIcon>
                  <SocialInputField
                    type="url"
                    value={formData.tiktok_url}
                    onChange={(e) => handleInputChange('tiktok_url', e.target.value)}
                    placeholder="https://tiktok.com/@username"
                  />
                </SocialInput>
              </FormGroup>
            </SocialMediaGrid>

            <ButtonGroup>
              <ActionButton onClick={closeModal}>İptal</ActionButton>
              <ActionButton className="primary" onClick={handleSave}>Kaydet</ActionButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </SettingsContainer>
    </>
  );
};

export default SettingsPage;
